import http from '../../../api/axios';
import { apiUrl } from '../../../api/apiUrl';
import DataTable from '../../../components/table/DataTable';
import { Payment, PaymentStatus } from '../../../types/Payment';
import { columns } from '../../../components/columns/Company/DocumentPaymentColumns';
import { useEffect, useState } from 'react';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { InvoiceBaseInputs } from '../../../types/Invoice';
import { toast } from 'react-toastify';
import Button from '../../../components/button/Button';
import { useTranslation } from '../../../context/translationContext';
import { PlusIcon } from '@heroicons/react/24/outline';
import { PaymentForm } from '../../../forms/Payment/PaymentForm';
import { Values } from '../../../forms/Payment/PaymentForm.types';
import { useLoading } from '../../../context/loadingContext';
import { handleApiErrors } from '../../../api/apiErrorHandler';
import { format } from 'date-fns';
import { dateSubmitFormat } from '../../../types/core/date.types';
import ListButton, { ListButtonEntityType } from '../../../components/listButton/ListButton';

interface Props {
  document: InvoiceBaseInputs;
}

/**
 * Invoice side panel for managing payments
 */
export const InvoiceSidePanelTabPayments = ({ document }: Props) => {
  const { t } = useTranslation();
  const { company } = useCurrentCompany();
  const [payments, setPayments] = useState<Payment[] | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const { startLoading, stopLoading } = useLoading();

  useEffect(() => {
    if (!company || !document) return;
    fetchPayments();
    fetchPaymentStatus();
  }, [company, document]);

  /**
   * Loads payment status for current document
   */
  const fetchPaymentStatus = async () => {
    if (!company) return;
    const req = `?documentId=${document.id}`;
    const response = await http.apiGet({
      url: `${apiUrl.ACCOUNTING_DOCUMENT_GET_PAYMENT_LIST}${req}`,
    });
    if (response?.data) setPaymentStatus(response?.data);
  };

  /**
   * Loads payments for current document
   */
  const fetchPayments = async () => {
    if (!company) return;
    const req = `?parentId=${document.id}`;
    const response = await http.apiGet({
      url: `${apiUrl.PAYMENT_LIST}${req}`,
    });
    if (response?.data) {
      const formattedPayments = response.data.map((payment: any) => {
        return {
          ...payment,
          paymentDate: payment.paymentDate
            ? format(new Date(payment.paymentDate), dateSubmitFormat)
            : format(new Date(), dateSubmitFormat),
        };
      });
      setPayments(formattedPayments);
    }
  };

  /**
   * Redirects to payment edit page
   * @param id
   */
  const onEditItem = (id: string | number) => {
    if (!id) return;
    setSelectedPayment(payments?.find((p) => p.id === id) || null);
    setShowForm(true);
  };

  /**
   * Removes payment
   * @param id of entity
   */
  const onRemoveItem = async (id: string | number) => {
    if (id === null) return;
    const response = await http.apiDelete({
      url: `${apiUrl.PAYMENT_DELETE}?id=${id}`,
    });
    if (response?.status === 204) {
      toast.success('Success!');
      fetchPayments();
      fetchPaymentStatus();
    }
  };

  /**
   * Handles edit action for payment
   * @param values form data
   */
  const handleUpdate = async (values: Values) => {
    try {
      startLoading();
      const response = await http.apiPut({ url: apiUrl.PAYMENT_UPDATE, data: values });
      if (response?.status === 200) {
        toast.success('Success!');
        fetchPayments();
        fetchPaymentStatus();
        setSelectedPayment(null);
        setShowForm(false);
        stopLoading();
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  /**
   * Handles create action for payment
   * @param values form data
   */
  const handleCreate = async (values: Values) => {
    try {
      startLoading();
      const response = await http.apiPost({ url: apiUrl.PAYMENT_CREATE, data: values });
      if (response?.status === 201) {
        fetchPayments();
        fetchPaymentStatus();
        toast.success('Success!');
        setShowForm(false);
        stopLoading();
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  const onSubmit = (e: any) => {
    if (e.id) return handleUpdate(e);
    return handleCreate(e);
  };

  return (
    <>
      {!showForm && paymentStatus && (
        <div>
          <div className="flex justify-between items-center">
            <div>
              <ListButton params={paymentStatus} entityType={ListButtonEntityType.PAYMENT_STATUS} />
            </div>
            <div className="text-right">
              <Button
                title={t('general.create-new')}
                color="green"
                icon={<PlusIcon className="w-4 h-4 mr-2" />}
                onClick={() => setShowForm(true)}
              />
            </div>
          </div>
          <DataTable
            data={payments ?? []}
            columns={columns(t)}
            handleRemove={onRemoveItem}
            handleEdit={onEditItem}
          />
        </div>
      )}
      {showForm && (
        <>
          {selectedPayment && selectedPayment.id ? (
            <h1 className="text-lg">{t('page.payment.headline-edit')}</h1>
          ) : (
            <h1 className="text-lg">{t('page.payment.headline-create')}</h1>
          )}
          <PaymentForm
            onSubmit={(e) => onSubmit(e)}
            initialValues={selectedPayment as Values}
            onBack={() => {
              setSelectedPayment(null);
              setShowForm(false);
            }}
            document={document}
          />
        </>
      )}
    </>
  );
};
